import { SiMeta, SiTiktok, SiYoutube } from "@icons-pack/react-simple-icons";
import { cn } from "@nexus/ui";

/**
 * Brand marks keyed by the ids `/mock-data/ad-platforms` serves — lowercase (`meta`,
 * `google`, `tiktok`), a separate vocabulary from preflight's uppercase `PreflightPlatformCode`,
 * so this map stays distinct from `PLATFORM_BRAND_ICONS`.
 */
const AD_PLATFORM_BRAND_ICONS = {
  // Matches the Meta blue preflight already paints, so one brand never gets two blues.
  meta: { Glyph: SiMeta, colorClassName: "text-[#0467DF]" },
  // The stored `google` id is the YouTube ad channel, so users get the YouTube mark and the
  // same red preflight paints it.
  google: { Glyph: SiYoutube, colorClassName: "text-[#FF0000]" },
  // Some tenants store the channel as `youtube`; `adPlatformLabel` already names it YouTube.
  youtube: { Glyph: SiYoutube, colorClassName: "text-[#FF0000]" },
  // TikTok's mark is black; the foreground token keeps it visible in dark mode.
  tiktok: { Glyph: SiTiktok, colorClassName: "text-foreground" },
} as const;

type BrandIconId = keyof typeof AD_PLATFORM_BRAND_ICONS;

// Normalised like `adPlatformLabel`, so an id that gets a brand name also gets its mark.
// Own keys only: an id like `constructor` must not resolve to an inherited Object member.
function toBrandIconId(platformId: string): BrandIconId | null {
  const id = platformId.trim().toLowerCase();
  return Object.hasOwn(AD_PLATFORM_BRAND_ICONS, id) ? (id as BrandIconId) : null;
}

export function hasAdPlatformBrandIcon(platformId: string): boolean {
  return toBrandIconId(platformId) !== null;
}

export interface AdPlatformIconProps {
  platformId: string;
  className?: string;
}

/**
 * A platform's brand mark in its brand colour. Renders nothing for an unrecognised id
 * rather than inventing a logo, so a new server-side platform degrades to a bare label.
 */
export function AdPlatformIcon({ platformId, className }: AdPlatformIconProps) {
  const iconId = toBrandIconId(platformId);
  if (!iconId) return null;

  const { Glyph, colorClassName } = AD_PLATFORM_BRAND_ICONS[iconId];
  return (
    <Glyph
      aria-hidden
      focusable={false}
      // simple-icons renders `<title>{brand}</title>` unless silenced, and Select mirrors
      // item content into its trigger — that would print the name twice next to the label.
      title=""
      className={cn("size-3.5 shrink-0", colorClassName, className)}
    />
  );
}
